import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const accessModule: ChecklistModuleDefinition = {
  code: 'access',
  name: 'Access & Roads',
  description: 'Road access, easements, and driveways',
  categories: [
    {
      code: 'road_access',
      name: 'Road Access',
      items: [
        {
          code: 'access_type',
          title: 'Access type identification',
          description: 'Identify how property is accessed',
          isGroupTask: false,
          dataFields: [
            { key: 'access_types', label: 'Access Types', type: 'multiselect', options: [
              { value: 'public_paved', label: 'Public Paved Road' },
              { value: 'public_gravel', label: 'Public Gravel Road' },
              { value: 'public_dirt', label: 'Public Dirt Road' },
              { value: 'private_road', label: 'Private Road' },
              { value: 'easement', label: 'Access Easement' },
              { value: 'landlocked', label: 'Landlocked/No Access' },
            ]},
            { key: 'primary_access', label: 'Primary Access', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Landlocked - no legal access' },
            { flag: 'big_green', condition: 'Paved road frontage' },
          ],
        },
        {
          code: 'road_frontage',
          title: 'Road frontage assessment',
          description: 'Measure and evaluate road frontage',
          isGroupTask: false,
          dataFields: [
            { key: 'frontage_feet', label: 'Road Frontage', type: 'number', unit: 'feet' },
            { key: 'road_name', label: 'Road Name', type: 'string' },
            { key: 'meets_minimum', label: 'Meets Minimum Requirements', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Insufficient road frontage' },
          ],
        },
        {
          code: 'road_condition',
          title: 'Road condition assessment',
          description: 'Evaluate condition of access road',
          isGroupTask: false,
          dataFields: [
            { key: 'surface_type', label: 'Surface Type', type: 'string' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
              { value: 'impassable', label: 'Impassable at times' },
            ]},
            { key: 'year_round_access', label: 'Year-Round Access', type: 'boolean' },
            { key: 'seasonal_issues', label: 'Seasonal Issues', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Road impassable portions of year' },
            { flag: 'red', condition: 'Poor road condition' },
            { flag: 'yellow', condition: 'Seasonal access concerns' },
          ],
        },
        {
          code: 'road_maintenance',
          title: 'Road maintenance responsibility',
          description: 'Who maintains the access road',
          isGroupTask: false,
          dataFields: [
            { key: 'maintained_by', label: 'Maintained By', type: 'select', options: [
              { value: 'state', label: 'State DOT' },
              { value: 'county', label: 'County' },
              { value: 'township', label: 'Township' },
              { value: 'hoa', label: 'HOA/Association' },
              { value: 'shared_private', label: 'Shared Private' },
              { value: 'owner', label: 'Property Owner' },
            ]},
            { key: 'maintenance_agreement', label: 'Maintenance Agreement Exists', type: 'boolean' },
            { key: 'annual_cost', label: 'Annual Cost Share', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Private road with no maintenance agreement' },
          ],
        },
      ],
    },
    {
      code: 'access_easements',
      name: 'Access Easements',
      items: [
        {
          code: 'access_easement_review',
          title: 'Access easement review',
          description: 'Review any access easement documents',
          isGroupTask: false,
          dataFields: [
            { key: 'easement_exists', label: 'Access Easement Exists', type: 'boolean' },
            { key: 'easement_type', label: 'Easement Type', type: 'select', options: [
              { value: 'express', label: 'Express (Recorded)' },
              { value: 'prescriptive', label: 'Prescriptive' },
              { value: 'implied', label: 'Implied' },
              { value: 'necessity', label: 'Easement by Necessity' },
            ]},
            { key: 'easement_width', label: 'Easement Width', type: 'number', unit: 'feet' },
            { key: 'recorded', label: 'Recorded with Deed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Access easement not recorded/unclear' },
            { flag: 'red', condition: 'Easement issues or disputes' },
            { flag: 'green', condition: 'Clear recorded easement' },
          ],
        },
        {
          code: 'easement_terms',
          title: 'Easement terms/restrictions',
          description: 'Understand any limitations in easement',
          isGroupTask: false,
          dataFields: [
            { key: 'use_restrictions', label: 'Use Restrictions', type: 'array' },
            { key: 'vehicle_limitations', label: 'Vehicle Limitations', type: 'string' },
            { key: 'improvement_rights', label: 'Rights to Improve', type: 'boolean' },
            { key: 'maintenance_obligations', label: 'Maintenance Obligations', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Restrictive easement terms' },
          ],
        },
      ],
    },
    {
      code: 'driveway',
      name: 'Driveway',
      items: [
        {
          code: 'driveway_existing',
          title: 'Existing driveway assessment',
          description: 'Evaluate current driveway',
          isGroupTask: false,
          dataFields: [
            { key: 'driveway_exists', label: 'Driveway Exists', type: 'boolean' },
            { key: 'length', label: 'Length', type: 'number', unit: 'feet' },
            { key: 'width', label: 'Width', type: 'number', unit: 'feet' },
            { key: 'surface', label: 'Surface Type', type: 'select', options: [
              { value: 'paved', label: 'Paved/Asphalt' },
              { value: 'concrete', label: 'Concrete' },
              { value: 'gravel', label: 'Gravel' },
              { value: 'dirt', label: 'Dirt' },
            ]},
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
          ],
        },
        {
          code: 'driveway_permit',
          title: 'Driveway permit status',
          description: 'Check if driveway properly permitted',
          isGroupTask: false,
          dataFields: [
            { key: 'permit_required', label: 'Permit Required', type: 'boolean' },
            { key: 'permit_exists', label: 'Permit Exists', type: 'boolean' },
            { key: 'meets_standards', label: 'Meets Road Standards', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Driveway not permitted but should be' },
          ],
        },
        {
          code: 'new_driveway_cost',
          title: 'New driveway estimate',
          description: 'Cost to build/improve driveway if needed',
          isGroupTask: false,
          dataFields: [
            { key: 'work_needed', label: 'Work Needed', type: 'string' },
            { key: 'estimated_cost', label: 'Estimated Cost', type: 'number', unit: 'dollars' },
            { key: 'permit_cost', label: 'Permit Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'culvert_crossing',
          title: 'Culvert/crossing requirements',
          description: 'Drainage crossings needed',
          isGroupTask: false,
          dataFields: [
            { key: 'culvert_needed', label: 'Culvert Needed', type: 'boolean' },
            { key: 'culvert_exists', label: 'Culvert Exists', type: 'boolean' },
            { key: 'culvert_condition', label: 'Culvert Condition', type: 'string' },
            { key: 'replacement_cost', label: 'Replacement Cost', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
    {
      code: 'internal_access',
      name: 'Internal Access',
      items: [
        {
          code: 'internal_roads',
          title: 'Internal roads/trails',
          description: 'Assess roads and trails within property',
          isGroupTask: false,
          dataFields: [
            { key: 'miles_of_roads', label: 'Miles of Roads', type: 'number', unit: 'miles' },
            { key: 'road_types', label: 'Road Types', type: 'array' },
            { key: 'overall_condition', label: 'Overall Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'maintenance_needed', label: 'Maintenance Needed', type: 'string' },
          ],
        },
        {
          code: 'property_accessibility',
          title: 'Property accessibility',
          description: 'Can you access all parts of property',
          isGroupTask: false,
          dataFields: [
            { key: 'all_accessible', label: 'All Areas Accessible', type: 'boolean' },
            { key: 'inaccessible_areas', label: 'Inaccessible Areas', type: 'string' },
            { key: 'vehicle_type_needed', label: 'Vehicle Type Needed', type: 'select', options: [
              { value: 'car', label: 'Standard Car' },
              { value: 'suv', label: 'SUV/Truck' },
              { value: '4wd', label: '4WD Required' },
              { value: 'atv', label: 'ATV/UTV Only' },
              { value: 'foot', label: 'Foot Traffic Only' },
            ]},
          ],
        },
      ],
    },
  ],
};

